import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGame } from '../net/GameProvider';
import { Button } from './ui';

/** How long the confirm state stays armed before reverting. */
const ARMED_MS = 3000;

/**
 * Backs out of a room — or out of an invite that has not been accepted yet.
 *
 * Leaving a room is two-tap: mid-game it forfeits your hearts and your seat, and
 * the button sits in the top bar on every screen, where a stray tap is easy. It
 * disarms after a few seconds so it never gets stuck looking like a warning.
 *
 * Dismissing an invite is one tap, because nothing is lost — you simply had not
 * joined yet. Without it, a share link is a dead end: the code field is hidden,
 * so the only escape is hand-editing the URL.
 */
export function LeaveRoomButton() {
  const { t } = useTranslation();
  const { actions, state, pendingCode } = useGame();
  const [armed, setArmed] = useState(false);

  // Not in a room yet — this is an unaccepted invite, so no confirm step.
  const invitePending = state === null && pendingCode !== null;

  useEffect(() => {
    if (!armed) return;
    const timer = window.setTimeout(() => setArmed(false), ARMED_MS);
    return () => window.clearTimeout(timer);
  }, [armed]);

  return (
    <Button
      type="button"
      $tone={armed ? 'danger' : 'ghost'}
      $size="sm"
      aria-label={armed ? t('common.leaveConfirm') : t('common.leave')}
      onClick={() => {
        if (invitePending) return actions.dismissInvite();
        if (armed) return actions.leave();
        setArmed(true);
      }}
    >
      {armed ? `👋 ${t('common.leaveConfirm')}` : `🚪 ${t('common.leave')}`}
    </Button>
  );
}
